import { PERMISSION_CODES } from '@/constants/permissions';

const toPrefix = (code: string): string => {
  const firstColonIndex = code.indexOf(':');
  if (firstColonIndex === -1) {
    return `${code}:`;
  }

  return `${code.slice(0, firstColonIndex + 1)}`;
};

const uniqueMatchers = (...matchers: string[]): string[] => {
  return Array.from(new Set(matchers.filter(Boolean)));
};

/**
 * Central configuration for permission system
 * This ensures consistency across the entire application
 */

export const PERMISSION_CONFIG = {
  // Enable debug logging only in development
  enableDebugLogging: process.env.NODE_ENV === 'development',

  // Cache duration for permissions (in milliseconds)
  permissionCacheDuration: 5 * 60 * 1000, // 5 minutes

  // Retry configuration
  maxRetries: 3,
  retryDelay: 1000,

  // Module permission mappings
  modulePermissionPrefixes: {
    financeModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.FINANCE_OVERVIEW_VIEW),
      toPrefix(PERMISSION_CODES.FINANCE_BANK_ACCOUNTS_VIEW),
      toPrefix(PERMISSION_CODES.FINANCE_BANK_TRANSACTIONS_VIEW),
      toPrefix(PERMISSION_CODES.FINANCE_COA_VIEW),
      toPrefix(PERMISSION_CODES.FINANCE_RECONCILIATION_VIEW),
      toPrefix(PERMISSION_CODES.FINANCE_JOURNAL_VIEW),
      toPrefix(PERMISSION_CODES.FINANCE_REPORTS_VIEW),
    ),
    productModule: uniqueMatchers(
      PERMISSION_CODES.PRODUCT_EXPIRY_DATE_ERROR_VIEW,
      PERMISSION_CODES.PRODUCT_EXPIRY_DATE_ERROR_APPROVE,
      toPrefix(PERMISSION_CODES.PRODUCT_VIEW),
      PERMISSION_CODES.CATEGORY_VIEW,
      'category_review:view',
      toPrefix(PERMISSION_CODES.BRAND_VIEW),
      toPrefix(PERMISSION_CODES.BRAND_CREATE_EDIT),
      toPrefix(PERMISSION_CODES.BRAND_DELETE),
      toPrefix(PERMISSION_CODES.PRODUCT_DESCRIPTION_MANAGE_VIEW),
      toPrefix(PERMISSION_CODES.PRODUCT_DESCRIPTION_MANAGE_EDIT),
      toPrefix(PERMISSION_CODES.DISCOUNT_VIEW),
      toPrefix(PERMISSION_CODES.PRICE_CHANGE_VIEW),
      toPrefix(PERMISSION_CODES.HANSHOW_ESL_VIEW),
      toPrefix(PERMISSION_CODES.HANSHOW_ESL_MANAGE),
      toPrefix(PERMISSION_CODES.SUPPLY_PRODUCT_VIEW),
      toPrefix(PERMISSION_CODES.FEATURED_PRODUCT_VIEW),
      toPrefix(PERMISSION_CODES.BANNED_PRODUCTS_VIEW),
      toPrefix(PERMISSION_CODES.EXPIRED_PRODUCT_MANAGEMENT_VIEW),
      toPrefix(PERMISSION_CODES.UNSALABLE_PRODUCT_REPORT_VIEW),
      toPrefix(PERMISSION_CODES.UNSALABLE_PRODUCT_REPORT_CREATE),
      toPrefix(PERMISSION_CODES.UNSALABLE_PRODUCT_REPORT_FOLLOW_UP),
      toPrefix(PERMISSION_CODES.NEW_ARRIVAL_MANAGEMENT_VIEW),
      toPrefix(PERMISSION_CODES.NO_BARCODE_PRODUCT_VIEW),
      toPrefix(PERMISSION_CODES.NO_BARCODE_PRODUCT_MANAGE),
      toPrefix(PERMISSION_CODES.PRODUCT_STOCK_TYPE_MANAGEMENT),
      toPrefix(PERMISSION_CODES.SHIPMENT_SPEC_VIEW),
      toPrefix(PERMISSION_CODES.PRICE_CHANGE_APPROVE),
      toPrefix(PERMISSION_CODES.PRODUCT_DISPLAYS_VIEW),
      toPrefix(PERMISSION_CODES.PRODUCT_CHANGE_LOG_VIEW),
    ),
    purchaseModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.PURCHASE_OPPORTUNITY_VIEW),
      toPrefix(PERMISSION_CODES.PURCHASE_QUOTE_VIEW),
      toPrefix(PERMISSION_CODES.PURCHASE_ORDER_VIEW),
      toPrefix(PERMISSION_CODES.PURCHASE_ORDER_PAYMENT_VOUCHER_VIEW),
      PERMISSION_CODES.PURCHASE_CATEGORY_ARRIVAL_CALENDAR_VIEW,
    ),
    supplierModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.SUPPLIER_VIEW),
      toPrefix(PERMISSION_CODES.SUPPLIER_PLATFORM_GROUP_VIEW),
      toPrefix(PERMISSION_CODES.SUPPLIER_PLATFORM_MESSAGE_VIEW),
      toPrefix(PERMISSION_CODES.SUPPLIER_FOLLOW_UP_VIEW),
      toPrefix(PERMISSION_CODES.SUPPLIER_FOLLOW_UP_MANAGE),
      toPrefix(PERMISSION_CODES.SUPPLIER_DEVELOPMENT_VIEW),
      toPrefix(PERMISSION_CODES.SUPPLIER_DEVELOPMENT_CREATE_EDIT),
      toPrefix(PERMISSION_CODES.SUPPLIER_DEVELOPMENT_LINK_CONVERT),
      toPrefix(PERMISSION_CODES.SUPPLIER_CALLS_VIEW),
      toPrefix(PERMISSION_CODES.SUPPLIER_CALLS_CREATE),
      toPrefix(PERMISSION_CODES.SUPPLIER_CALLS_UPDATE_NOTES),
      toPrefix(PERMISSION_CODES.SUPPLIER_CALLS_LISTEN_RECORDING),
    ),
    storeModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.STORE_VIEW),
      toPrefix(PERMISSION_CODES.STORE_TARGETS_VIEW),
      toPrefix(PERMISSION_CODES.STORE_TARGETS_EDIT),
      toPrefix(PERMISSION_CODES.CATEGORY_PLACEMENT_VIEW),
      toPrefix(PERMISSION_CODES.PDA_ACCOUNT_VIEW),
    ),
    // Store Operations (Store Tasks Management)
    storeOperationsModule: uniqueMatchers(
      toPrefix('store_tasks_management:view'),
      toPrefix(PERMISSION_CODES.STORE_INSPECTION_QUESTION_MANAGEMENT_VIEW),
      toPrefix('store_support_tickets:view'),
      toPrefix('store_support_tickets:reply_archive'),
      toPrefix('customer_complaints:view'),
      toPrefix('roaster_records:view'),
      toPrefix(PERMISSION_CODES.PDA_DAILY_INVENTORY_CHECK_VIEW),
    ),
    inventoryModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.INVENTORY_OVERVIEW_VIEW),
      toPrefix(PERMISSION_CODES.INVENTORY_HISTORY_VIEW),
      toPrefix(PERMISSION_CODES.INVENTORY_CHANGE_LOG_VIEW),
      toPrefix(PERMISSION_CODES.INVENTORY_TASK_QUEUE_VIEW),
      toPrefix(PERMISSION_CODES.INVENTORY_CHECK_FOLLOWUPS_VIEW),
      toPrefix(PERMISSION_CODES.INVENTORY_LOSS_VIEW),
      toPrefix(PERMISSION_CODES.INVENTORY_FINANCIAL_LOSS_DASHBOARD_VIEW),
      toPrefix(PERMISSION_CODES.PRODUCT_DAMAGE_REPORT_VIEW),
      toPrefix(PERMISSION_CODES.STOCKTAKE_VIEW),
      toPrefix(PERMISSION_CODES.STOCKTAKE_CREATE_EDIT),
      toPrefix(PERMISSION_CODES.STOCKTAKE_DELETE),
      toPrefix(PERMISSION_CODES.WAREHOUSE_STRUCTURE_ANALYSIS_VIEW),
      toPrefix(PERMISSION_CODES.STORE_STRUCTURE_ANALYSIS_VIEW),
      toPrefix(PERMISSION_CODES.INVENTORY_STORE_ANALYSIS_VIEW),
      toPrefix(PERMISSION_CODES.INVENTORY_EXPIRY_ANALYSIS_VIEW),
      toPrefix(PERMISSION_CODES.INVENTORY_UNSOLD_PRODUCTS_VIEW),
      toPrefix(PERMISSION_CODES.INVENTORY_TURNOVER_ANALYSIS_VIEW),
      PERMISSION_CODES.INVENTORY_RECEIVE,
      PERMISSION_CODES.INVENTORY_UNSOLD_PRODUCTS_EXPORT,
    ),
    posModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.STORE_ACCOUNT_VIEW),
      toPrefix(PERMISSION_CODES.PAYMENT_METHOD_VIEW),
      toPrefix(PERMISSION_CODES.POS_APP_VERSION_VIEW),
      toPrefix(PERMISSION_CODES.POS_SESSION_VIEW),
      toPrefix(PERMISSION_CODES.POS_OPERATION_LOG_VIEW),
      toPrefix(PERMISSION_CODES.POS_RECEIPT_CONTENT_VIEW),
      toPrefix(PERMISSION_CODES.POS_QUICK_ACTION_VIEW),
      toPrefix(PERMISSION_CODES.POS_BEA_CONFIG_VIEW),
      toPrefix(PERMISSION_CODES.POS_BEA_CONFIG_EDIT),
    ),
    onlineStoreModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.ONLINE_PRODUCT_VIEW),
      PERMISSION_CODES.ONLINE_ORDER_VIEW,
      PERMISSION_CODES.ONLINE_ORDER_EDIT,
      PERMISSION_CODES.ONLINE_ORDER_CASH_REFUND,
      PERMISSION_CODES.ONLINE_ORDER_ORIGINAL_REFUND,
      PERMISSION_CODES.ONLINE_ORDER_FOLLOWUP_VIEW,
      PERMISSION_CODES.ONLINE_STORE_BEHAVIOUR_VIEW,
      PERMISSION_CODES.ONLINE_STORE_DISCOUNT_CODE_VIEW,
      PERMISSION_CODES.ONLINE_STORE_CUSTOMER_MESSAGE_VIEW,
      PERMISSION_CODES.ONLINE_STORE_BLOG_POST_VIEW,
      PERMISSION_CODES.ONLINE_STORE_BLOG_POST_CREATE_EDIT,
      PERMISSION_CODES.ONLINE_STORE_BLOG_POST_DELETE,
    ),
    marketingManagementModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.MARKETING_MANAGEMENT_VIEW),
      toPrefix(PERMISSION_CODES.MARKETING_MANAGEMENT_MANAGE),
    ),
    marketInfoModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.COLLECTION_TASK_VIEW),
      toPrefix(PERMISSION_CODES.MI_PRODUCT_VIEW),
      toPrefix(PERMISSION_CODES.DATA_REVIEW_VIEW),
      toPrefix(PERMISSION_CODES.DATA_ANALYSIS_VIEW),
      toPrefix(PERMISSION_CODES.PRICE_ANALYSIS_VIEW),
      toPrefix(PERMISSION_CODES.MI_USER_VIEW),
      toPrefix(PERMISSION_CODES.CONVERT_TO_OPPORTUNITY),
    ),
    userManagementModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.USER_VIEW),
      toPrefix(PERMISSION_CODES.TEAM_VIEW),
      toPrefix(PERMISSION_CODES.ROLE_VIEW),
      toPrefix(PERMISSION_CODES.AUDIT_VIEW),
      toPrefix(PERMISSION_CODES.USER_PRODUCT_VIEW_LOG_VIEW),
      toPrefix(PERMISSION_CODES.USER_NOTIFICATION_LOG_VIEW),
      toPrefix(PERMISSION_CODES.MOBILE_USER_MANAGEMENT_MANAGE),
      toPrefix(PERMISSION_CODES.FRANCHISEE_MANAGEMENT_ACCOUNT_VIEW),
      toPrefix(PERMISSION_CODES.FRANCHISEE_MANAGEMENT_ACCOUNT_VIEW_LEGACY),
      toPrefix(PERMISSION_CODES.FRANCHISEE_ACCOUNT_VIEW),
      toPrefix(PERMISSION_CODES.WHOLESALE_CUSTOMER_VIEW),
    ),
    hrManagementModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.HR_DASHBOARD_VIEW),
      toPrefix(PERMISSION_CODES.HR_EMPLOYEES_VIEW),
      toPrefix(PERMISSION_CODES.HR_EMPLOYEES_CREATE_EDIT),
      toPrefix(PERMISSION_CODES.HR_EMPLOYEES_DELETE),
      toPrefix(PERMISSION_CODES.HR_EMPLOYEES_MANAGE_ALL),
      toPrefix(PERMISSION_CODES.HR_ROSTER_VIEW),
      toPrefix(PERMISSION_CODES.HR_ROSTER_CREATE_EDIT),
      toPrefix(PERMISSION_CODES.HR_ROSTER_CLOCK_CREATE_EDIT),
      toPrefix(PERMISSION_CODES.HR_LEAVE_VIEW),
      toPrefix(PERMISSION_CODES.HR_LEAVE_CREATE_EDIT),
      toPrefix(PERMISSION_CODES.HR_LEAVE_CANCEL),
      toPrefix(PERMISSION_CODES.HR_SALARIES_VIEW),
      toPrefix(PERMISSION_CODES.HR_SALARIES_CREATE_EDIT),
      toPrefix(PERMISSION_CODES.HR_SALARIES_MANAGE_ALL),
      toPrefix(PERMISSION_CODES.HR_ATTENDANCE_STATS_VIEW),
      toPrefix(PERMISSION_CODES.HR_WORK_HOUR_ANOMALIES_VIEW),
      toPrefix(PERMISSION_CODES.HR_WORK_HOUR_HISTORY_VIEW),
      toPrefix(PERMISSION_CODES.HR_AUDIT_LOGS_VIEW),
    ),
    scriptModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.SCRIPT_VIEW),
      toPrefix(PERMISSION_CODES.SCRIPT_EXECUTION_LOG_VIEW),
    ),
    dataAnomalyModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.DATA_CHECKS_VIEW),
      toPrefix(PERMISSION_CODES.BAD_PRODUCT_NAME_VIEW),
      toPrefix(PERMISSION_CODES.BAD_PRODUCT_NAME_UPDATE),
      toPrefix(PERMISSION_CODES.CATEGORY_ERRORS_VIEW),
      toPrefix(PERMISSION_CODES.MISSING_CATEGORIES_VIEW),
      toPrefix(PERMISSION_CODES.MISSING_BRAND_VIEW),
      toPrefix(PERMISSION_CODES.MISSING_BRAND_UPDATE),
      toPrefix(PERMISSION_CODES.MISSING_DESCRIPTION_VIEW),
      toPrefix(PERMISSION_CODES.MISSING_DESCRIPTION_UPDATE),
      toPrefix(PERMISSION_CODES.DUPLICATE_PRODUCTS_MANAGE),
      toPrefix(PERMISSION_CODES.DUPLICATE_BARCODE_VIEW),
      toPrefix(PERMISSION_CODES.PRODUCT_EXPIRY_DATE_ERROR_VIEW),
      toPrefix(PERMISSION_CODES.PRODUCT_EXPIRY_DATE_ERROR_APPROVE),
      toPrefix(PERMISSION_CODES.PRODUCT_STOCK_TYPE_MANAGEMENT),
    ),
    salesAnalysisModule: uniqueMatchers(
      PERMISSION_CODES.DATA_DASHBOARD_VIEW,
      PERMISSION_CODES.PRODUCT_SALES_ANALYSIS_VIEW,
      PERMISSION_CODES.PRODUCT_SALES_ANALYSIS_EXPORT,
      PERMISSION_CODES.PRODUCT_SPECIFIC_SALES_VIEW,
      PERMISSION_CODES.PRODUCT_SPECIFIC_SALES_ASSIGN,
      PERMISSION_CODES.CATEGORY_SALES_VIEW,
      PERMISSION_CODES.CATEGORY_SALES_CHART_VIEW,
      PERMISSION_CODES.CATEGORY_SALES_EXPORT,
      PERMISSION_CODES.PAYMENT_GATEWAY_VIEW,
      PERMISSION_CODES.SUPPLIER_SALES_REPORT_VIEW,
      PERMISSION_CODES.SUPPLIER_SALES_REPORT_EXPORT,
      PERMISSION_CODES.FRANCHISE_STORE_SALES_ANALYSIS_VIEW,
      PERMISSION_CODES.GROSS_MARGIN_ANALYSIS_VIEW,
    ),
    warehouseTransferModule: uniqueMatchers(
      PERMISSION_CODES.INVENTORY_ANOMALY_DASHBOARD_VIEW,
      toPrefix(PERMISSION_CODES.WAREHOUSE_DASHBOARD_VIEW),
      toPrefix(PERMISSION_CODES.TRANSFER_ORDER_VIEW),
      toPrefix(PERMISSION_CODES.TRANSFER_EXCEPTION_FOLLOWUP_VIEW),
      toPrefix(PERMISSION_CODES.TRANSFER_PICKING_PROGRESS_VIEW),
      toPrefix(PERMISSION_CODES.TRANSFER_ORDER_CREATE_EDIT),
      toPrefix(PERMISSION_CODES.TRANSFER_ORDER_DELETE),
      toPrefix(PERMISSION_CODES.TRANSFER_SHIPPING_SCHEDULE_VIEW),
      toPrefix(PERMISSION_CODES.TRANSFER_SHIPPING_SCHEDULE_CREATE_EDIT),
      PERMISSION_CODES.WAREHOUSE_PURCHASE_ORDER_ARRIVAL_SCHEDULE_VIEW,
      toPrefix(PERMISSION_CODES.PRODUCT_TRANSFER_RECORD_VIEW),
      toPrefix(PERMISSION_CODES.TRANSFER_PRODUCT_SEARCH_VIEW),
    ),
    categoryModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.CATEGORY_VIEW),
      toPrefix(PERMISSION_CODES.CATEGORY_EDIT),
      PERMISSION_CODES.CATEGORY_EXPORT,
      PERMISSION_CODES.MANAGE_WAREHOUSE_SKU_LIMITS,
    ),
    orderModule: uniqueMatchers(
      PERMISSION_CODES.ORDER_VIEW,
      PERMISSION_CODES.ORDER_REFUND,
      PERMISSION_CODES.ORDER_RECONCILIATION_VIEW,
      PERMISSION_CODES.WHOLESALE_ORDER_VIEW,
      PERMISSION_CODES.WHOLESALE_ORDER_CREATE,
      PERMISSION_CODES.EMPLOYEE_PURCHASE_REFUND_VIEW,
      PERMISSION_CODES.EMPLOYEE_PURCHASE_REFUND_APPROVE,
    ),
    procurementAnalyticsModule: uniqueMatchers(
      PERMISSION_CODES.PROCUREMENT_ANALYSIS,
      toPrefix(PERMISSION_CODES.PROCUREMENT_ANALYSIS_DASHBOARD_VIEW),
      toPrefix(PERMISSION_CODES.PROCUREMENT_ANALYSIS_MODULE_VIEW),
      toPrefix(PERMISSION_CODES.PROCUREMENT_ANALYSIS_DATA_ERRORS_VIEW),
      toPrefix(PERMISSION_CODES.PROCUREMENT_ANALYSIS_HISTORY_PERFORMANCE_VIEW),
      toPrefix(PERMISSION_CODES.PROCUREMENT_ANALYSIS_SMALL_TRIAL_SALE_FOLLOWUP_VIEW),
      toPrefix(PERMISSION_CODES.PROCUREMENT_ANALYSIS_OPPORTUNITY_VIEW),
      toPrefix(PERMISSION_CODES.REPLENISHMENT_VIEW),
      toPrefix(PERMISSION_CODES.PURCHASE_CATEGORY_ARRIVAL_CALENDAR_VIEW),
    ),
    franchiseManagementModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.FRANCHISEE_ACCOUNT_VIEW),
      toPrefix(PERMISSION_CODES.FRANCHISEE_MANAGEMENT_ACCOUNT_VIEW),
      toPrefix(PERMISSION_CODES.FRANCHISEE_MANAGEMENT_ACCOUNT_VIEW_LEGACY),
    ),
    // Ensure franchisee management menu works with both legacy and current prefixes
    franchiseeManagementModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.FRANCHISEE_MANAGEMENT_ACCOUNT_VIEW),
      toPrefix(PERMISSION_CODES.FRANCHISEE_MANAGEMENT_ACCOUNT_VIEW_LEGACY),
      toPrefix(PERMISSION_CODES.FRANCHISEE_ACCOUNT_VIEW),
    ),
    memberManagementModule: uniqueMatchers(
      toPrefix(PERMISSION_CODES.MEMBER_VIEW),
      toPrefix(PERMISSION_CODES.MEMBER_COUPON_VIEW),
      toPrefix(PERMISSION_CODES.COUPON_TEMPLATE_VIEW),
    ),
  },
} as const;

const MODULE_PERMISSION_EXCLUSIONS: Partial<
  Record<keyof typeof PERMISSION_CONFIG.modulePermissionPrefixes, string[]>
> = {
  // This permission is stored under the inventory namespace for API
  // compatibility, but belongs to the warehouse-transfer module.
  inventoryModule: [PERMISSION_CODES.INVENTORY_ANOMALY_DASHBOARD_VIEW],
};

/**
 * Check if a user has permission for a module based on their permission codes
 */
export function hasModulePermission(
  moduleKey: keyof typeof PERMISSION_CONFIG.modulePermissionPrefixes,
  permissions: string[],
): boolean {
  const prefixes = PERMISSION_CONFIG.modulePermissionPrefixes[moduleKey];
  if (!prefixes) return false;
  const exclusions = MODULE_PERMISSION_EXCLUSIONS[moduleKey] || [];

  return permissions.some(
    (permission) =>
      !exclusions.some(
        (excluded) =>
          permission === excluded ||
          permission.startsWith(`${excluded}:`) ||
          permission.startsWith(`${excluded}|`),
      ) && prefixes.some((prefix) => permission.startsWith(prefix)),
  );
}

/**
 * Get all modules the user has access to
 */
export function getAccessibleModules(permissions: string[]): string[] {
  const modules: string[] = [];

  Object.entries(PERMISSION_CONFIG.modulePermissionPrefixes).forEach(([module, prefixes]) => {
    const exclusions =
      MODULE_PERMISSION_EXCLUSIONS[
        module as keyof typeof PERMISSION_CONFIG.modulePermissionPrefixes
      ] || [];
    if (
      permissions.some(
        (permission) =>
          !exclusions.some(
            (excluded) =>
              permission === excluded ||
              permission.startsWith(`${excluded}:`) ||
              permission.startsWith(`${excluded}|`),
          ) && prefixes.some((prefix) => permission.startsWith(prefix)),
      )
    ) {
      modules.push(module);
    }
  });

  return modules;
}
